import { useCallback, useEffect, useState } from "react";
import { Download, Plus, Save, Trash2, Upload } from "lucide-react";
import { useToast } from "@/shared/providers/ToastProvider";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { StatusBadge } from "../components/StatusBadge";
import { FormField, inputClass, selectClass } from "../components/FormField";
import type { NetworkHospital } from "../api/insurerApi";
import {
  fetchClaimsGrid,
  fetchClaimsGrids,
  fetchClaimsNetwork,
  importGridActs,
  saveClaimsGrid,
  saveGridActs,
  seedGridFromCatalogue,
  type CareCategoryOption,
  type GridUnit,
  type TariffGrid,
  type TariffGridAct,
} from "../api/claimsApi";

const buttonClass =
  "inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-60";
const secondaryClass =
  "inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm text-foreground transition-colors hover:bg-accent/20 hover:border-accent disabled:opacity-60";

type GridDraft = {
  id?: number;
  code: string;
  name: string;
  valid_from: string;
  valid_to: string;
  currency: "USD" | "CDF";
  status: "active" | "inactive";
};

const EMPTY_GRID: GridDraft = { code: "", name: "", valid_from: "", valid_to: "", currency: "USD", status: "active" };

const EMPTY_ACT: TariffGridAct = { act_code: "", act_name: "", care_category: null, price: 0, unit: "per_act" };

/**
 * Modèle d'import de grille : une ligne par acte négocié, catégorie et unité avec les valeurs acceptées
 * par l'import (catégorie facultative). S'ouvre dans Excel ; à réenregistrer en CSV (point-virgule).
 */
const GRID_TEMPLATE = [
  "code;nom;categorie;prix;unite",
  "CS-GEN;Consultation médecine générale;consultation;20;per_act",
  "CS-SPE;Consultation spécialiste;consultation;30;per_act",
  "LAB-GE;Goutte épaisse;laboratoire;8;per_act",
  "LAB-NFS;Numération formule sanguine;laboratoire;12;per_act",
  "ECHO-ABD;Échographie abdominale;imagerie;40;per_act",
  "PANS;Pansement simple;nursing;5;per_act",
  "PHA-PARA;Paracétamol 500 mg (boîte);pharmacie;2,50;per_act",
  "CH-COM;Chambre commune;hospitalisation;75;per_day",
  "KINE;Séance de kinésithérapie;support_reeducation;15;per_session",
  "ACC-SIMP;Accouchement simple;gyneco_obstetrique;250;flat",
].join("\r\n");

function downloadGridTemplate(): void {
  const blob = new Blob(["﻿" + GRID_TEMPLATE + "\r\n"], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "modele-grille-tarifaire.csv";
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Admin et Sinistre : grilles tarifaires négociées par l'assureur actif avec les prestataires de son réseau.
 * Un même prestataire peut avoir une grille différente chez un autre assureur.
 */
export function ClaimsGridsPage() {
  const { showToast } = useToast();
  const [network, setNetwork] = useState<NetworkHospital[]>([]);
  const [hospitalId, setHospitalId] = useState<number | null>(null);
  const [grids, setGrids] = useState<TariffGrid[]>([]);
  const [units, setUnits] = useState<Record<string, string>>({});
  const [categories, setCategories] = useState<CareCategoryOption[]>([]);
  const [draft, setDraft] = useState<GridDraft>(EMPTY_GRID);
  const [acts, setActs] = useState<TariffGridAct[]>([]);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchClaimsNetwork()
      .then((rows) => {
        setNetwork(rows);
        // Ouverture depuis la fiche assureur : prestataire choisi dans l'adresse (?hospital=ID).
        const wanted = Number(new URLSearchParams(window.location.search).get("hospital"));
        setHospitalId(rows.find((row) => row.hospital_id === wanted)?.hospital_id ?? rows[0]?.hospital_id ?? null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Chargement impossible."));
  }, []);

  const selectGrid = useCallback(async (grid: TariffGrid | null) => {
    setError(null);
    if (!grid) {
      setDraft(EMPTY_GRID);
      setActs([]);
      return;
    }
    const full = await fetchClaimsGrid(grid.id);
    setDraft({
      id: full.id,
      code: full.code,
      name: full.name,
      valid_from: full.valid_from,
      valid_to: full.valid_to ?? "",
      currency: full.currency,
      status: full.status,
    });
    setActs(full.acts ?? []);
  }, []);

  const loadGrids = useCallback(async (id: number) => {
    const data = await fetchClaimsGrids(id);
    setGrids(data.grids);
    setUnits(data.units);
    setCategories(data.care_categories);
    return data.grids;
  }, []);

  useEffect(() => {
    if (!hospitalId) return;
    loadGrids(hospitalId)
      .then((rows) => selectGrid(rows[0] ?? null))
      .catch((err) => setError(err instanceof Error ? err.message : "Chargement impossible."));
  }, [hospitalId, loadGrids, selectGrid]);

  const run = async (action: () => Promise<void>, success: string) => {
    setSaving(true);
    setError(null);
    try {
      await action();
      showToast(success);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement impossible.");
    } finally {
      setSaving(false);
    }
  };

  const saveGrid = () =>
    run(async () => {
      if (!hospitalId) return;
      const saved = await saveClaimsGrid(
        {
          hospital_id: hospitalId,
          code: draft.code,
          name: draft.name,
          valid_from: draft.valid_from,
          valid_to: draft.valid_to || null,
          currency: draft.currency,
          status: draft.status,
        },
        draft.id,
      );
      await loadGrids(hospitalId);
      await selectGrid(saved);
    }, "Grille enregistrée.");

  const saveActs = () =>
    run(async () => {
      if (!draft.id) return;
      const saved = await saveGridActs(draft.id, acts);
      setActs(saved.acts ?? []);
      if (hospitalId) await loadGrids(hospitalId);
    }, "Actes enregistrés.");

  const importCsv = () =>
    run(async () => {
      if (!draft.id || !importFile) return;
      const { result, grid } = await importGridActs(draft.id, importFile);
      setActs(grid.acts ?? []);
      setImportFile(null);
      if (result.errors.length > 0) setError(`${result.errors.length} ligne(s) ignorée(s) : ${result.errors.slice(0, 3).join(" ")}`);
      if (hospitalId) await loadGrids(hospitalId);
    }, "Actes importés.");

  const seedCatalogue = () =>
    run(async () => {
      if (!draft.id) return;
      const { grid } = await seedGridFromCatalogue(draft.id);
      setActs(grid.acts ?? []);
      if (hospitalId) await loadGrids(hospitalId);
    }, "Catalogue du prestataire repris.");

  const setAct = (index: number, patch: Partial<TariffGridAct>) =>
    setActs((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <>
      <Breadcrumbs items={[{ label: "Grilles tarifaires" }]} />
      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <FormField label="Prestataire">
          <select id="grid-hospital" value={hospitalId ?? ""} onChange={(e) => setHospitalId(e.target.value ? Number(e.target.value) : null)} className={selectClass}>
            {network.length === 0 ? <option value="">Aucun prestataire dans le réseau</option> : null}
            {network.map((hospital) => (
              <option key={hospital.hospital_id} value={hospital.hospital_id}>{hospital.name}</option>
            ))}
          </select>
        </FormField>
        <div className="flex flex-wrap gap-2">
          {grids.map((grid) => (
            <button
              key={grid.id}
              type="button"
              onClick={() => void selectGrid(grid)}
              className={`rounded-lg border px-3 py-2 text-sm ${grid.id === draft.id ? "border-primary bg-primary/5" : "border-border hover:bg-accent/20 hover:border-accent"}`}
            >
              {grid.code} · {grid.acts_count} actes
            </button>
          ))}
          <button type="button" className={secondaryClass} onClick={() => void selectGrid(null)}>
            <Plus size={14} />
            Nouvelle grille
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        <section className="theme-card p-5">
          <h1 className="mb-4 text-lg font-semibold text-foreground">{draft.id ? `Grille ${draft.code}` : "Nouvelle grille"}</h1>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
            <FormField label="Code" required>
              <input id="grid-code" value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })} className={inputClass} />
            </FormField>
            <div className="md:col-span-2">
              <FormField label="Nom" required>
                <input id="grid-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={inputClass} />
              </FormField>
            </div>
            <FormField label="Valide du" required>
              <input id="grid-from" type="date" value={draft.valid_from} onChange={(e) => setDraft({ ...draft, valid_from: e.target.value })} className={inputClass} />
            </FormField>
            <FormField label="Au" hint="Vide = sans fin">
              <input id="grid-to" type="date" value={draft.valid_to} onChange={(e) => setDraft({ ...draft, valid_to: e.target.value })} className={inputClass} />
            </FormField>
            <FormField label="Devise">
              <select id="grid-currency" value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value as GridDraft["currency"] })} className={selectClass}>
                <option value="USD">USD</option>
                <option value="CDF">CDF</option>
              </select>
            </FormField>
          </div>
          <button
            type="button"
            className={`${buttonClass} mt-4`}
            disabled={saving || !hospitalId || !draft.code.trim() || !draft.name.trim() || !draft.valid_from}
            onClick={() => void saveGrid()}
          >
            <Save size={15} />
            Enregistrer la grille
          </button>
        </section>

        {draft.id ? (
          <section className="theme-card flex flex-col gap-4 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-foreground">Actes ({acts.length})</h2>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" className={secondaryClass} disabled={saving} onClick={() => void seedCatalogue()}>
                  <Download size={14} />
                  Reprendre le catalogue du prestataire
                </button>
                <button type="button" className={secondaryClass} onClick={downloadGridTemplate}>
                  <Download size={14} />
                  Télécharger le modèle
                </button>
                <input id="grid-import-file" type="file" accept=".csv,text/csv" className="text-sm" onChange={(e) => setImportFile(e.target.files?.[0] ?? null)} />
                <button type="button" className={secondaryClass} disabled={saving || !importFile} onClick={() => void importCsv()}>
                  <Upload size={14} />
                  Importer (CSV)
                </button>
              </div>
            </div>
            <p className="text-xs text-muted">
              CSV (séparateur point-virgule) avec l'en-tête : code ; nom ; categorie ; prix ; unite. Les codes déjà présents sont mis à jour.
              Catégorie (facultative) : consultation, pharmacie, imagerie, laboratoire, nursing, chirurgie, hospitalisation, gyneco_obstetrique,
              ophtalmologie, dentisterie, support_reeducation, transport. Unité : per_act (par acte), per_day (par jour), per_hour (par heure),
              per_session (par séance), flat (forfait).
            </p>

            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-card-muted text-left text-xs uppercase text-muted">
                  <tr>
                    <th className="px-2 py-2">Code acte</th>
                    <th className="px-2 py-2">Nom de l'acte</th>
                    <th className="px-2 py-2">Prix</th>
                    <th className="px-2 py-2">Détail</th>
                    <th className="px-2 py-2">Catégorie</th>
                    <th className="px-2 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {acts.map((act, index) => (
                    <tr key={index} className="border-t border-border">
                      <td className="px-2 py-1.5">
                        <input aria-label="Code acte" value={act.act_code} onChange={(e) => setAct(index, { act_code: e.target.value.toUpperCase() })} className={`${inputClass} h-8 w-28`} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input aria-label="Nom de l'acte" value={act.act_name} onChange={(e) => setAct(index, { act_name: e.target.value })} className={`${inputClass} h-8 min-w-[14rem]`} />
                      </td>
                      <td className="px-2 py-1.5">
                        <input aria-label="Prix" type="number" min={0} step="0.01" value={act.price} onChange={(e) => setAct(index, { price: Number(e.target.value) })} className={`${inputClass} h-8 w-28`} />
                      </td>
                      <td className="px-2 py-1.5">
                        <select aria-label="Détail" value={act.unit} onChange={(e) => setAct(index, { unit: e.target.value as GridUnit })} className={`${selectClass} h-8`}>
                          {Object.entries(units).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <select aria-label="Catégorie" value={act.care_category ?? ""} onChange={(e) => setAct(index, { care_category: e.target.value || null })} className={`${selectClass} h-8`}>
                          <option value="">—</option>
                          {categories.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-1.5">
                        <button type="button" aria-label="Supprimer l'acte" className="rounded-lg p-1.5 text-muted hover:bg-card-muted hover:text-red-600" onClick={() => setActs((rows) => rows.filter((_, i) => i !== index))}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap justify-between gap-2">
              <button type="button" className={secondaryClass} onClick={() => setActs((rows) => [...rows, { ...EMPTY_ACT }])}>
                <Plus size={14} />
                Ajouter un acte
              </button>
              <button type="button" className={buttonClass} disabled={saving} onClick={() => void saveActs()}>
                <Save size={15} />
                Enregistrer les actes
              </button>
            </div>
            <div>
              <StatusBadge variant="info">Le Sinistre compare chaque facture à la grille en vigueur pendant la période facturée.</StatusBadge>
            </div>
          </section>
        ) : null}
      </div>
    </>
  );
}
